import React, { useEffect, useState } from 'react';
import { Save } from 'lucide-react';
import { toast } from 'sonner';
import adminApi from '../api/adminApi';
import ImagePicker from '../components/ImagePicker';
import { MENU_ITEMS, DEFAULT_MENU_CONFIG } from '../../lib/menuConfig';

const labelClass = "block text-xs uppercase tracking-wider text-[#D4AF37] font-semibold mb-2";
const Card = ({ title, description, children }) => (
  <div className="bg-[#111111] border border-[rgba(212,175,55,0.15)] rounded-sm p-5 space-y-4">
    <div className="border-b border-[rgba(212,175,55,0.1)] pb-3">
      <h2 className="text-sm font-semibold text-white">{title}</h2>
      {description && <p className="text-white/40 text-xs mt-0.5">{description}</p>}
    </div>
    {children}
  </div>
);

const SettingsPage = () => {
  const [global, setGlobal] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminApi.get('/admin/content').then(r => {
      setGlobal(r.data?.global || {});
    }).catch(() => toast.error('Failed to load settings'));
  }, []);

  const set = (key, value) => setGlobal(g => ({ ...g, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminApi.patch('/admin/content/global', global);
      toast.success('Settings saved');
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-white">Settings</h1>
          <p className="text-white/40 text-sm mt-0.5">Logo, theme defaults, and language</p>
        </div>
        <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-5 py-2.5 bg-[#D4AF37] text-black font-semibold text-sm rounded-sm hover:bg-[#C5A028] transition-colors disabled:opacity-50">
          <Save className="w-4 h-4" />{saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      <Card title="Logo" description="Logos displayed in the website header and footer">
        <ImagePicker label="Logo (Dark Mode)" value={global.logo_url_dark || ''} onChange={v => set('logo_url_dark', v)} />
        <ImagePicker label="Logo (Light Mode)" value={global.logo_url_light || ''} onChange={v => set('logo_url_light', v)} />
      </Card>

      <Card title="Default Theme" description="The theme shown to visitors on their first visit">
        <div className="grid grid-cols-2 gap-3">
          {[{ value: 'dark', label: 'Dark Mode', desc: 'Black background, gold accents' }, { value: 'light', label: 'Light Mode', desc: 'Cream background, dark accents' }].map(option => (
            <label key={option.value} className={`flex items-start gap-3 p-4 rounded-sm border cursor-pointer transition-colors ${global.default_theme === option.value ? 'border-[#D4AF37] bg-[rgba(212,175,55,0.1)]' : 'border-[rgba(212,175,55,0.15)] hover:border-[rgba(212,175,55,0.3)]'}`}>
              <input type="radio" name="theme" value={option.value} checked={global.default_theme === option.value} onChange={() => set('default_theme', option.value)} className="accent-[#D4AF37] mt-0.5" />
              <div>
                <p className="text-white text-sm font-medium">{option.label}</p>
                <p className="text-white/40 text-xs">{option.desc}</p>
              </div>
            </label>
          ))}
        </div>
      </Card>

      <Card title="Default Language" description="The language shown to visitors on their first visit">
        <div className="grid grid-cols-2 gap-3">
          {[{ value: 'en', label: 'English', desc: 'Left-to-right layout' }, { value: 'ar', label: 'Arabic (عربي)', desc: 'Right-to-left layout' }].map(option => (
            <label key={option.value} className={`flex items-start gap-3 p-4 rounded-sm border cursor-pointer transition-colors ${global.default_language === option.value ? 'border-[#D4AF37] bg-[rgba(212,175,55,0.1)]' : 'border-[rgba(212,175,55,0.15)] hover:border-[rgba(212,175,55,0.3)]'}`}>
              <input type="radio" name="language" value={option.value} checked={global.default_language === option.value} onChange={() => set('default_language', option.value)} className="accent-[#D4AF37] mt-0.5" />
              <div>
                <p className="text-white text-sm font-medium">{option.label}</p>
                <p className="text-white/40 text-xs">{option.desc}</p>
              </div>
            </label>
          ))}
        </div>
      </Card>

      <Card title="Sections Visibility" description="Show or hide each section on the public website">
        <div className="space-y-2">
          {MENU_ITEMS.map(item => {
            const enabled = (global.menu?.[item.key]?.enabled ?? DEFAULT_MENU_CONFIG[item.key]?.enabled) !== false;
            const toggle = () => set('menu', {
              ...DEFAULT_MENU_CONFIG,
              ...(global.menu || {}),
              [item.key]: { enabled: !enabled },
            });
            return (
              <label key={item.key} className={`flex items-center justify-between gap-4 p-4 rounded-sm border cursor-pointer transition-colors ${enabled ? 'border-[#D4AF37] bg-[rgba(212,175,55,0.07)]' : 'border-[rgba(212,175,55,0.15)] hover:border-[rgba(212,175,55,0.3)]'}`}>
                <div>
                  <p className={`text-sm font-medium ${enabled ? 'text-white' : 'text-white/50'}`}>{item.label}</p>
                  <p className="text-white/35 text-xs">{item.description}</p>
                </div>
                <div className="relative flex-shrink-0">
                  <input type="checkbox" checked={enabled} onChange={toggle} className="sr-only" />
                  <div
                    onClick={toggle}
                    className={`w-10 h-5 rounded-full transition-colors duration-200 ${enabled ? 'bg-[#D4AF37]' : 'bg-white/15'}`}
                  >
                    <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200 ${enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                  </div>
                </div>
              </label>
            );
          })}
        </div>
      </Card>
    </div>
  );
};

export default SettingsPage;
